import { inject, Injectable, signal } from "@angular/core";
import { setupL10n } from '@inscomponents/core';
import { AnnouncementsNotificator, KsefMonitorService, RootContextConfig, SaleFeatureFlagsRulesService, UserRoleSynchronizerService } from "@skylab-angular-shared/business";
import { ApplicationInsightsService, FeatureFlagsService, TelemetryLoggerService } from "@skylab-angular-shared/config";
import { L10n, RootContext, RootContextBase, RoutingService } from "@skylab-angular-shared/core";
import { ThemeSwitcherService } from "@skylab-angular-shared/ui";
import { AnnouncementsNotificatorBase, FeatureAvailability, ModuleAvailability, PermissionAvailability, SaleFeatureFlagsRulesServiceBase, UserRoleSynchronizerServiceBase } from "@skylab-shared/business";
import { CacheConfiguration, FeatureFlagsLoader, TranslationLoader, UserLanguageLoader } from "@skylab-shared/config";
import { ConsoleLogger, L10nBase } from "@skylab-shared/core";
import { setGlobalDateI18n } from "fecha";
import { concatMap, delay, from, last, tap } from "rxjs";

export type AppInitState = 'NotStarted' | 'Running' | 'Success' | 'Failed';

@Injectable({
  providedIn: 'root'
})
export class AppInitDataService {
	public _state = signal<AppInitState>('NotStarted');
	public _progress = signal(0);
	public state = this._state.asReadonly();
	public progress = this._progress.asReadonly();

	readonly #LOADER_FINISHED_DELAY = 500;

	#userRoleSynchronizer = inject(UserRoleSynchronizerService) as UserRoleSynchronizerServiceBase;
	#rootContext = inject(RootContext as any) as RootContextBase;
	#rootContextConfig = inject(RootContextConfig);
	#ksefMonitor = inject(KsefMonitorService);
	#announcementService = inject(AnnouncementsNotificator) as AnnouncementsNotificatorBase;
	#saleFeatureFlagsRulesService = inject(SaleFeatureFlagsRulesService) as SaleFeatureFlagsRulesServiceBase;
	#telemetryLogger = inject(TelemetryLoggerService);
	#featureFlagsService = inject(FeatureFlagsService);
	#analitycs = inject(ApplicationInsightsService);
	#l10n = inject(L10n) as L10nBase;
	#routingService = inject(RoutingService);
	#themeSwitcherService = inject(ThemeSwitcherService);
	#logger = new ConsoleLogger(this.constructor.name);

	private steps: (() => Promise<any>)[] = [
		() => Promise.resolve(CacheConfiguration.configure()),
		() => this.#themeSwitcherService.loadAsync(),
		() => TranslationLoader.loadAsync(),
		() => Promise.resolve(this.setupWebcomponentsTranslations()),
		() => Promise.resolve(this.setFechaLocales()),
		() => Promise.resolve(this.#routingService.init()),
		() => this.#userRoleSynchronizer.synchronizeAsync(),
		() => FeatureFlagsLoader.loadAsync().then(data => this.#featureFlagsService.flags = data),
		() => this.#analitycs.registerAsync(),
		() => Promise.resolve(this.#saleFeatureFlagsRulesService.configure()),
		() => this.#rootContextConfig.registerAsync(),
		() => FeatureAvailability.registerAsync(this.#featureFlagsService),
		() => ModuleAvailability.registerAsync(this.#rootContext),
		() => PermissionAvailability.registerAsync(this.#rootContext),
		() => this.#announcementService.refreshAsync(),
		() => this.#ksefMonitor.refreshAsync(),
		() => Promise.resolve(this.#telemetryLogger.log('AppStart')),
	];


	public run(): Promise<any> {
		if (this._state() === 'Success') return Promise.resolve(true);

		this._state.set('Running');

		return new Promise((resolve, reject) => {
			from(this.steps).pipe(
				concatMap((stepFn, index) =>
					from(stepFn()).pipe(
						tap({
							next: () => {
								const percent = Math.round(((index + 1) / this.steps.length) * 100);
								this._progress.set(percent);
							},
							error: (err) => {
								this.#logger.error(err, 'App preload failed!');
							}
						})
					)
				),
				last(),
				delay(this.#LOADER_FINISHED_DELAY)
			).subscribe({
				complete: () => {
					this._state.set('Success');
					resolve(true);
				},
				error: (err) => {
					this._state.set('Failed');
					reject(err);
				}
			});
		});
	}

	private setupWebcomponentsTranslations(): void {
		setupL10n({
			language: UserLanguageLoader.language === 'en' ? 'en-GB' : UserLanguageLoader.language,
			datepicker: {
				quarter: this.#l10n._T('ui.date.calendar.quarter'),
				today: this.#l10n._T('ui.date.calendar.current-day'),
				currentMonth: this.#l10n._T('ui.date.calendar.current-month'),
				currentQuarter: this.#l10n._T('ui.date.calendar.current-quarter')
			},
			filePicker: {
				pickFile: this.#l10n._T('ui.file-picker.pick-file'),
				pickFiles: this.#l10n._T('ui.file-picker.pick-files')
			},
			input: {
				clearButton: this.#l10n._T('ui.input.clearButton')
			}
		});
	}

	private setFechaLocales(): void {
		setGlobalDateI18n({
			dayNames: [
				this.#l10n._T('calendar.day-names.sunday'),
				this.#l10n._T('calendar.day-names.monday'),
				this.#l10n._T('calendar.day-names.tuesday'),
				this.#l10n._T('calendar.day-names.wednesday'),
				this.#l10n._T('calendar.day-names.thursday'),
				this.#l10n._T('calendar.day-names.friday'),
				this.#l10n._T('calendar.day-names.saturday'),
			]
		});
		setGlobalDateI18n({
			dayNamesShort: [
				this.#l10n._T('calendar.day-names-short.sunday'),
				this.#l10n._T('calendar.day-names-short.monday'),
				this.#l10n._T('calendar.day-names-short.tuesday'),
				this.#l10n._T('calendar.day-names-short.wednesday'),
				this.#l10n._T('calendar.day-names-short.thursday'),
				this.#l10n._T('calendar.day-names-short.friday'),
				this.#l10n._T('calendar.day-names-short.saturday'),
			]
		});
		setGlobalDateI18n({
			monthNamesShort: [
				this.#l10n._T('calendar.month-names-short.january'),
				this.#l10n._T('calendar.month-names-short.february'),
				this.#l10n._T('calendar.month-names-short.march'),
				this.#l10n._T('calendar.month-names-short.april'),
				this.#l10n._T('calendar.month-names-short.may'),
				this.#l10n._T('calendar.month-names-short.june'),
				this.#l10n._T('calendar.month-names-short.july'),
				this.#l10n._T('calendar.month-names-short.august'),
				this.#l10n._T('calendar.month-names-short.september'),
				this.#l10n._T('calendar.month-names-short.october'),
				this.#l10n._T('calendar.month-names-short.november'),
				this.#l10n._T('calendar.month-names-short.december'),
			]
		});
		setGlobalDateI18n({
			amPm: [this.#l10n._T('calendar.am-pm.am'), this.#l10n._T('calendar.am-pm.pm')]
		});
		setGlobalDateI18n({
			monthNames: [
				this.#l10n._T('calendar.month-names.january'),
				this.#l10n._T('calendar.month-names.february'),
				this.#l10n._T('calendar.month-names.march'),
				this.#l10n._T('calendar.month-names.april'),
				this.#l10n._T('calendar.month-names.may'),
				this.#l10n._T('calendar.month-names.june'),
				this.#l10n._T('calendar.month-names.july'),
				this.#l10n._T('calendar.month-names.august'),
				this.#l10n._T('calendar.month-names.september'),
				this.#l10n._T('calendar.month-names.october'),
				this.#l10n._T('calendar.month-names.november'),
				this.#l10n._T('calendar.month-names.december'),
			]
		});
	}
}
